// GA4 session split by channel, with the derived AI-assistants channel.
// Used compact on the dashboard ("Where they came from") and full-size on
// the Attribution screen (adds share bars).

import { Link } from 'react-router-dom';
import { Card, Eyebrow, Dot } from '../ui';

// Known channel groups get stable brand colors; anything else cycles.
const CHANNEL_COLORS = {
  'Organic Search': '#022889',
  Direct: '#2596BE',
  Referral: '#006D78',
  'Organic Social': '#3EAAD0',
  'AI assistants': '#FF6E83',
  Email: '#B7791F',
};
const FALLBACK_COLORS = ['#022889', '#2596BE', '#006D78', '#3EAAD0', '#B7791F', '#FF6E83'];

function channelColor(channel, i) {
  return CHANNEL_COLORS[channel] || FALLBACK_COLORS[i % FALLBACK_COLORS.length];
}

export default function ChannelSplitCard({ traffic, compact = false, maxRows = 6 }) {
  const data = traffic?.data;
  const unavailable = !data || data.configured === false;
  const channels = (data?.channels || []).slice(0, maxRows);
  const ai = data?.channels?.find(c => c.channel === 'AI assistants');
  const aiSources = (data?.aiSources || []).slice(0, 3).map(s => s.source).join(', ');

  return (
    <Card lash="navy" className="p-4 flex-1">
      <div className="flex items-center justify-between mb-2.5">
        <Eyebrow>Where they came from</Eyebrow>
        {compact && (
          <Link to="/marketing/attribution" className="text-bullogic-blue text-[12px] font-bold">
            Attribution →
          </Link>
        )}
      </div>

      {traffic?.loading && <div className="text-[12px] text-ink-3">Loading GA4…</div>}

      {!traffic?.loading && unavailable && (
        <div className="text-[12px] text-ink-2">
          {traffic?.error
            ? `GA4 traffic unavailable: ${traffic.error}`
            : 'GA4 is not configured. Set GA4_PROPERTY_ID and connect Google from the SEO screen.'}
        </div>
      )}

      {!traffic?.loading && !unavailable && (
        <div className="flex flex-col gap-2">
          {channels.map((c, i) => (
            <div key={c.channel}>
              <div className="flex items-center justify-between gap-3">
                <span className="flex items-center gap-2 font-semibold text-[13px] text-ink">
                  <Dot color={channelColor(c.channel, i)} />
                  {c.channel}
                </span>
                <span className="tabular-nums text-[13px]">
                  <b className="text-navy">{Math.round(c.pct * 100)}%</b>
                  <span className="text-ink-2 ml-2">{c.sessions.toLocaleString()}</span>
                </span>
              </div>
              {!compact && (
                <div className="h-[7px] rounded-[4px] bg-line-2 overflow-hidden mt-1">
                  <div
                    className="h-full rounded-[4px]"
                    style={{ width: `${Math.max(c.pct * 100, 2)}%`, backgroundColor: channelColor(c.channel, i) }}
                  />
                </div>
              )}
            </div>
          ))}
          {ai && ai.sessions > 0 && (
            <div className="mt-1 bg-bullogic-blue/5 border border-dashed border-bullogic-blue/40 rounded-[7px] px-3 py-2 text-[12px] text-ink-2">
              <b className="text-navy">{Math.round(ai.pct * 100)}% from AI assistants</b>
              {aiSources ? ` (${aiSources})` : ''}. GA4 buckets these under Referral or Direct; we split them out.
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
